// Theme context + MUI provider. Owns mode (light/dark) and card style, mirrors
// them onto <html data-theme data-card> so the CSS-variable layer in
// variables.css reacts, and feeds the matching MUI theme to MUI components.
// Persists choices to localStorage. No Redux dependency — theme is pure UI
// concern and components read it via the useThemeBridge() hook.
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { buildTheme } from './muiTheme';
import { STORAGE_KEYS } from '../config/constants';

const ThemeBridgeContext = createContext(null);

const read = (key, fallback) => {
  try {
    return localStorage.getItem(key) || fallback;
  } catch {
    return fallback;
  }
};

// Card style is coupled to the theme mode per the finalized design choice
// (light → Pearl + Elevated, dark → Cloud + Inset). We ship only the Pearl
// palette, so just the card-style half applies.
const cardStyleForMode = (mode) => (mode === 'dark' ? 'inset' : 'elevated');

export function ThemeBridgeProvider({ children }) {
  const [mode, setModeState] = useState(() => read(STORAGE_KEYS.THEME_MODE, 'light'));
  // Derive the initial card style from the initial mode so a returning dark-mode
  // user gets inset cards immediately (no first-paint flash of elevated).
  const [cardStyle, setCardStyleState] = useState(() => cardStyleForMode(read(STORAGE_KEYS.THEME_MODE, 'light')));

  // Reflect onto the document so CSS tokens switch, and persist. Keep card style
  // in lockstep with the mode (KitchenSink can still override between switches).
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', mode);
    setCardStyleState(cardStyleForMode(mode));
    try {
      localStorage.setItem(STORAGE_KEYS.THEME_MODE, mode);
    } catch { /* storage may be unavailable */ }
  }, [mode]);

  useEffect(() => {
    document.documentElement.setAttribute('data-card', cardStyle);
    try {
      localStorage.setItem(STORAGE_KEYS.CARD_STYLE, cardStyle);
    } catch { /* storage may be unavailable */ }
  }, [cardStyle]);

  const theme = useMemo(() => buildTheme(mode), [mode]);

  const value = useMemo(
    () => ({
      mode,
      setMode: setModeState,
      toggleMode: () => setModeState((m) => (m === 'dark' ? 'light' : 'dark')),
      cardStyle,
      setCardStyle: setCardStyleState,
    }),
    [mode, cardStyle],
  );

  return (
    <ThemeBridgeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeBridgeContext.Provider>
  );
}

ThemeBridgeProvider.propTypes = {
  children: PropTypes.node,
};

export const useThemeBridge = () => {
  const ctx = useContext(ThemeBridgeContext);
  if (!ctx) throw new Error('useThemeBridge must be used within <ThemeBridgeProvider>');
  return ctx;
};

export default ThemeBridgeProvider;
